/**
 * Pure-SVG sparkline. Zero dependencies — the panel already pulls in
 * React + Vite, and adding a charting library for one chart would
 * triple the bundle.
 *
 * The chart auto-fits to its container width via viewBox/preserve.
 * Each data point hovers a small dot so it stays readable on cold
 * (mostly-zero) days, and the latest data point is highlighted to
 * call attention to "today".
 */
export function Sparkline({
  values,
  labels,
  height = 64,
}: {
  values: number[];
  labels?: string[];
  height?: number;
}) {
  if (!values || values.length === 0) {
    return <div className="muted">No data.</div>;
  }
  const w = Math.max(values.length * 16, 240);
  const h = height;
  const max = Math.max(1, ...values);
  const min = 0;
  const step = w / Math.max(1, values.length - 1);

  // Pre-compute (x, y) for every point so we can use them in both
  // the area fill <path> and the per-point dots.
  const points = values.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / (max - min)) * (h - 12) - 6;
    return { x, y, v };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${(w).toFixed(1)},${h} L0,${h} Z`;

  const last = points[points.length - 1];
  const total = values.reduce((s, v) => s + v, 0);
  const lastLabel = labels && labels.length ? labels[labels.length - 1] : '';

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        width="100%"
        height={h}
        role="img"
        aria-label={`Sparkline of ${values.length} points, total ${total}`}
        style={{ display: 'block' }}
      >
        {/* baseline */}
        <line x1={0} y1={h - 0.5} x2={w} y2={h - 0.5} stroke="rgba(0,0,0,0.08)" />
        {/* filled area */}
        <path d={areaPath} fill="rgba(10,25,47,0.16)" />
        {/* line */}
        <path
          d={linePath}
          fill="none"
          stroke="var(--accent, #0a192f)"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* dots — keep dense charts readable */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={i === points.length - 1 ? 3.2 : 1.6}
            fill={i === points.length - 1 ? 'var(--accent, #0a192f)' : 'rgba(10,25,47,0.7)'}
          />
        ))}
      </svg>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <div className="muted" style={{ fontSize: 11 }}>
          {labels?.[0] ?? ''}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>
          {lastLabel} → {last.v}
        </div>
      </div>
    </div>
  );
}
